import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const BODY_PARTS = ['脚', '腕', '肩', '背中', '胸', '腹筋', '有酸素運動'];
const COLORS = {
  '脚': '#f97316', // Orange
  '腕': '#3b82f6', // Blue
  '肩': '#a855f7', // Purple
  '背中': '#10b981', // Emerald
  '胸': '#ef4444', // Red
  '腹筋': '#eab308', // Yellow
  '有酸素運動': '#06b6d4' // Cyan
};

const PeriodComparisonChart = ({ logs, period }) => {
  const { t } = useTranslation();
  const JP_TO_KEY = {
    '脚': 'legs', '腕': 'arms', '肩': 'shoulders', '背中': 'back', '胸': 'chest', '腹筋': 'abs', '有酸素運動': 'cardio'
  };

  const data = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    let thisPeriodStart, thisPeriodEnd;
    let lastPeriodStart, lastPeriodEnd;

    if (period === 'week') {
      // This Week (Monday start)
      const day = today.getDay();
      const diff = today.getDate() - (day === 0 ? 6 : day - 1); // Monday=0, Sunday=6
      thisPeriodStart = new Date(today);
      thisPeriodStart.setDate(diff);

      thisPeriodEnd = new Date(thisPeriodStart);
      thisPeriodEnd.setDate(thisPeriodStart.getDate() + 6);

      // Last Week
      lastPeriodStart = new Date(thisPeriodStart);
      lastPeriodStart.setDate(thisPeriodStart.getDate() - 7);

      lastPeriodEnd = new Date(lastPeriodStart);
      lastPeriodEnd.setDate(lastPeriodStart.getDate() + 6);
    } else {
      // This Month
      thisPeriodStart = new Date(today.getFullYear(), today.getMonth(), 1);
      thisPeriodEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);

      // Last Month
      lastPeriodStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      lastPeriodEnd = new Date(today.getFullYear(), today.getMonth(), 0);
    }

    const processLogs = (start, end) => {
      const counts = BODY_PARTS.reduce((acc, part) => ({ ...acc, [part]: 0 }), {});

      logs.forEach(log => {
        const logDate = new Date(log.date);
        // Reset time for accurate comparison
        logDate.setHours(0, 0, 0, 0);

        if (logDate >= start && logDate <= end) {
          if (log.workout_sets) {
            log.workout_sets.forEach(set => {
              if (set.exercise && set.exercise.body_part) {
                if (counts[set.exercise.body_part] !== undefined) {
                  counts[set.exercise.body_part] += (set.sets || 1);
                }
              }
            });
          }
        }
      });
      return counts;
    };

    const thisData = processLogs(thisPeriodStart, thisPeriodEnd);
    const lastData = processLogs(lastPeriodStart, lastPeriodEnd);

    return [
      { name: period === 'week' ? t('last_week') : t('last_month'), ...lastData },
      { name: period === 'week' ? t('this_week') : t('this_month'), ...thisData },
    ];
  }, [logs, period, t]);

  return (
    <div className="w-full h-64" style={{ minHeight: '250px' }}>
      <ResponsiveContainer width="100%" height="100%" minHeight={250} debounce={50}>
        <BarChart
          data={data}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis dataKey="name" stroke="#9ca3af" />
          <YAxis stroke="#9ca3af" />
          <Tooltip
            contentStyle={{ backgroundColor: '#1f2937', borderColor: '#374151', color: '#fff' }}
            itemStyle={{ color: '#fff' }}
          />
          <Legend />
          {BODY_PARTS.map(part => (
            <Bar key={part} dataKey={part} name={t(`body_parts.${JP_TO_KEY[part]}`)} stackId="a" fill={COLORS[part]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PeriodComparisonChart;
